export type CommissionRole = 'vendedor' | 'colaborador' | 'sdr' | 'gerente' | 'diretor' | 'socio_diretor' | 'override';

export const ROLE_LABELS: Record<CommissionRole, string> = {
    vendedor:      'Vendedor',
    colaborador:  'Colaborador',
    sdr:           'SDR',
    gerente:       'Gerente',
    diretor:       'Diretor Comercial',
    socio_diretor: 'Sócio-Diretor Comercial',
    override: 'Override Operacional',
};

export const ROLE_ORDER: CommissionRole[] = ['vendedor', 'colaborador', 'sdr', 'gerente', 'diretor', 'socio_diretor'];

export const STATUS_LABELS: Record<string, string> = {
    pending: 'Pendente',
    awaiting_nf: 'Aguardando NF',
    nf_submitted: 'NF enviada',
    paid: 'Pago',
    cancelled: 'Cancelado',
};

export type CommissionStructure = {
    id?: number;
    commission_role: CommissionRole;
    role_label: string;
    base_rate: number;
    seller_bonus_rate: number;
    cascade_rate: number;
    sdr_rate: number | null;
    active: boolean;
    notes: string | null;
};

export type CommissionModifier = {
    id: number;
    name: string;
    type: 'defrater' | 'accelerator';
    type_label: string;
    applies_to: CommissionRole | 'all';
    condition_type: 'meta_range' | 'product_type' | 'payment_term' | 'entry_range' | 'entry_accelerator' | 'deal_amount_min' | 'overfloor_per_pct' | 'manual';
    condition_label: string;
    condition_params: Record<string, unknown> | null;
    multiplier: number;
    delta_rate: number | null;
    per_pct_above_floor: number | null;
    active: boolean;
    sort_order: number;
    notes: string | null;
};

export type SalesTeamMemberRow = {
    id: number;
    user_id: number;
    user_name: string;
    user_email: string;
    user_avatar_url?: string | null;
    commission_role: CommissionRole;
    role_label: string;
    sdr_id: number | null;
    sdr_name: string | null;
    fixed_rate: number | null;
    sdr_share_rate: number | null;
    is_dual_role: boolean;
    skip_manager_bonus?: boolean;
    effective_from?: string | null;
    effective_to?: string | null;
    team_id?: number | null;
    team_name?: string | null;
    team_manager_id?: number | null;
    team_manager_name?: string | null;
    // Gerente explícito do período (sobrescreve team_manager na comissão)
    period_manager_id?: number | null;
    period_manager_name?: string | null;
    effective_manager_id?: number | null;
    effective_manager_name?: string | null;
};

export type SellerAssignmentPeriod = {
    id?: number | null;
    from: string | null;
    to: string | null;
    team_id: number | null;
    team_name: string | null;
    manager: { id: number; name: string } | null;
    commission_role: string | null;
    is_open?: boolean;
    is_gap: boolean;
};

export type SellerAssignmentHistory = {
    user_id: number;
    user_name: string | null;
    periods: SellerAssignmentPeriod[];
};

export type CommissionTeamMode = 'standard' | 'fixed_team';

export type SalesTeam = {
    id: number;
    name: string;
    commission_mode: CommissionTeamMode;
    active: boolean;
    notes: string | null;
    manager: { id: number; name: string; email?: string; avatar_url?: string | null } | null;
    director: { id: number; name: string; avatar_url?: string | null } | null;
    members: SalesTeamMemberRow[];
};

export type CommissionOverride = {
    id: number;
    beneficiary_user_id: number;
    beneficiary_name: string;
    scope_type: 'seller' | 'team_manager';
    scope_user_id: number;
    scope_user_name: string;
    deal_rate: number;
    active: boolean;
    notes: string | null;
};

export type CommissionRecord = {
    id: number;
    ploomes_deal_id: number;
    deal_number: string | null;
    internal_number?: string | null;
    display_sale_number?: string | null;
    deal_title: string | null;
    deal_amount: number | null;
    payment_parcelas?: number | null;
    installments_paid?: number;
    installment_label?: string | null;
    commission_installment_id?: number | null;
    installment_number?: number | null;
    user_id: number;
    user_name: string;
    commission_role: CommissionRole;
    role_label: string;
    base_amount: number;
    gross_amount: number;
    final_amount: number;
    modifiers_applied: { id: number; name: string; multiplier: number }[];
    status: string;
    status_label: string;
    status_color: string;
    invoice_path: string | null;
    invoice_submitted_at: string | null;
    paid_at: string | null;
    paid_by_name: string | null;
    confidential: boolean;
    notes: string | null;
    nf_deadline_at: string | null;
    payment_period: string | null;
    nf_reminder_count: number;
    commission_monthly_nf_id: number | null;
    days_until_nf_deadline: number | null;
    mode_flags?: { key: string; label: string }[];
    /** Competência do vendedor (YYYY-MM) — due/pago cliente + 1 mês. */
    competence_month?: string | null;
    client_name?: string | null;
    client_city?: string | null;
    client_state?: string | null;
    product_tags?: string[];
    created_at: string;
};

export type PaymentApprovalModeFlag = {
    key: string;
    label: string;
};

export type CommissionComposition = {
    deal_count: number;
    deal_amount: number;
    installment_count: number;
    installment_amount: number;
    salary_amount: number;
    current_period_count?: number;
    current_period_amount?: number;
    prior_sale_count?: number;
    prior_sale_amount?: number;
};

export type PaymentApprovalNfRecord = {
    id: number;
    source_type: 'deal' | 'installment';
    source_label: string;
    /** current_period = deal ganho no mês da NF · prior_sale = venda de mês anterior */
    deal_age?: 'current_period' | 'prior_sale' | 'unknown';
    deal_age_label?: string;
    period: string;
    date: string | null;
    deal_finish_date?: string | null;
    ploomes_deal_id: number;
    deal_number: string | null;
    deal_title: string | null;
    deal_amount: number | null;
    deal_url: string | null;
    client_name: string | null;
    payment_condition?: string | null;
    payment_parcelas?: number | null;
    /** Deal tem entrada distinta no parcelamento. */
    has_entry?: boolean;
    entry_pct?: number | null;
    commission_role: string;
    role_label: string;
    base_amount: number;
    gross_amount: number;
    final_amount: number;
    rate_pp: number | null;
    mode_flags: PaymentApprovalModeFlag[];
    commission_installment_id?: number | null;
    /** true = ordinal 0 (entrada); false = parcela regular; null = comissão de deal (não é parcela). */
    is_entry?: boolean | null;
    installment_kind?: 'entry' | 'installment' | null;
    installment_kind_label?: string | null;
    installment_number: number | null;
    total_installments: number | null;
    installment_amount?: number | null;
    installment_due_date?: string | null;
    installment_paid_at?: string | null;
    payment_method?: string | null;
    payment_method_label?: string | null;
    status: string;
    status_label: string;
    confidential: boolean;
    commission_monthly_nf_id: number | null;
};

export type PaymentApprovalSellerSummary = {
    user_id: number;
    user_name: string;
    user_email: string;
    user_avatar_url: string | null;
    job_title: string | null;
    monthly_nfs_count: number;
    monthly_nf_ids: number[];
    approvable_nf_ids: number[];
    records_count: number;
    total_base: number;
    total_commissions: number;
    total_salary: number;
    total_amount: number;
    avg_rate_pp: number | null;
    computed_commissions: number;
    totals_match: boolean;
    mismatch_amount: number;
    unlinked_records_count: number;
    unlinked_commissions: number;
    status_counts: {
        pending: number;
        awaiting_nf: number;
        nf_submitted: number;
        paid: number;
    };
    periods: string[];
    oldest_submitted_at: string | null;
    /** 'nf_submitted' = NF enviada aguardando aprovação | 'awaiting_nf' = NF mensal criada, aguardando envio | 'unconsolidated' = records sem NF mensal */
    nf_status: 'nf_submitted' | 'awaiting_nf' | 'unconsolidated';
    invoice_url: string | null;
    composition?: CommissionComposition;
    records: PaymentApprovalNfRecord[];
};

export type PaymentApprovalAuditLog = {
    id: number;
    action: string;
    description: string | null;
    changed_by: string | null;
    changed_by_id: number | null;
    old_values: Record<string, unknown> | null;
    new_values: Record<string, unknown> | null;
    created_at: string | null;
};

export type PaymentApprovalMonthlyNf = {
    id: number;
    period: string;
    period_label: string;
    status: string;
    status_label: string;
    total_commissions: number;
    total_salary: number;
    total_amount: number;
    computed_commissions: number;
    totals_match: boolean;
    mismatch_amount: number;
    records_count: number;
    composition?: CommissionComposition;
    invoice_url: string | null;
    invoice_submitted_at: string | null;
    nf_requested_at: string | null;
    nf_deadline_at: string | null;
    days_until_deadline: number | null;
    paid_at?: string | null;
    paid_by?: number | null;
    paid_by_name?: string | null;
    notes?: string | null;
    audit_logs?: PaymentApprovalAuditLog[];
    records: PaymentApprovalNfRecord[];
};

export type PaymentApprovalListResponse = {
    data: PaymentApprovalSellerSummary[];
    meta: {
        total_sellers: number;
        total_nfs: number;
        total_records: number;
        total_base: number;
        total_commissions: number;
        total_salary: number;
        total_amount: number;
        unconsolidated_amount: number;
        period: string | null;
        available_periods: string[];
        awaiting_nf_count: number;
        awaiting_nf_amount: number;
        awaiting_nf_sellers: number;
    };
};

export type PaymentApprovalDetailResponse = {
    seller: {
        id: number;
        name: string;
        email: string;
        job_title: string | null;
        avatar_url: string | null;
    };
    monthly_nfs: PaymentApprovalMonthlyNf[];
    records?: PaymentApprovalNfRecord[];
    unlinked_records_count?: number;
    composition?: CommissionComposition;
    summary: {
        monthly_nfs_count: number;
        records_count?: number;
        total_base?: number;
        total_commissions: number;
        total_salary: number;
        total_amount: number;
        avg_rate_pp?: number | null;
        totals_match?: boolean;
        mismatch_amount?: number;
        status_counts?: PaymentApprovalSellerSummary['status_counts'];
        composition?: CommissionComposition;
    };
};

export type AuditLogChange = {
    field: string;
    field_label: string;
    old: string | null;
    new: string | null;
};

export type AuditLog = {
    id: number;
    entity_type: string;
    entity_type_label: string;
    entity_id: number;
    changed_by: string;
    action: string;
    action_label: string;
    changes: AuditLogChange[];
    description: string | null;
    created_at: string;
};

export type AuditLogFilterOption = {
    value: string;
    label: string;
};

export type SalesCalendarDeal = {
    id: number;
    title: string;
    amount: number;
    owner_name: string | null;
    owner_avatar_url: string | null;
    client_name: string | null;
    internal_number: string | null;
    deal_number: string | null;
    segment: string | null;
    finish_date: string;
};

export type SalesCalendarDay = {
    date: string;
    deals_count: number;
    total_amount: number;
    deals: SalesCalendarDeal[];
};

export type SalesCalendarResponse = {
    days: SalesCalendarDay[];
    meta: {
        month: string;
        start: string;
        end: string;
        total_deals: number;
        total_amount: number;
        active_days: number;
        avg_per_active_day: number;
    };
};

export type CommissionSummary = {
    total_paid: number;
    total_pending: number;
    pending_nf_count: number;
    by_status: { status: string; status_label?: string; commission_role: string; total: number; count: number }[];
};

export type CommissionExecutiveKpis = {
    paid_mtd: number;
    paid_ytd: number;
    paid_prev_month: number;
    mom_paid_pct: number | null;
    accrued_mtd: number;
    pipeline_amount: number;
    awaiting_nf_count: number;
    awaiting_nf_amount: number;
    deals_mtd: number;
    deal_volume_mtd: number;
    avg_commission_mtd: number;
    effective_rate_mtd_pct: number | null;
};

export type CommissionExecutiveMonthly = {
    labels: string[];
    paid: number[];
    accrued: number[];
    deals: number[];
    deal_volume: number[];
    cumulative_paid: number[];
};

export type CommissionExecutiveByRole = {
    commission_role: string;
    role_label: string;
    paid: number;
    pipeline: number;
    total: number;
    count: number;
};

export type CommissionExecutiveByStatus = {
    status: string;
    status_label: string;
    count: number;
    amount: number;
};

export type CommissionExecutiveTopEarner = {
    user_id: number;
    name: string;
    paid: number;
    pipeline: number;
    records_count: number;
};

export type CommissionExecutiveDashboard = {
    kpis: CommissionExecutiveKpis;
    monthly: CommissionExecutiveMonthly;
    by_role: CommissionExecutiveByRole[];
    by_status: CommissionExecutiveByStatus[];
    top_earners: CommissionExecutiveTopEarner[];
};

export type SellerProfileUser = {
    id: number;
    name: string;
    email: string;
    job_title: string | null;
    avatar_url: string | null;
};

export type SellerProfileTeam = {
    team_id: number;
    team_name: string;
    commission_role: string;
    role_label: string;
    manager_name: string | null;
    director_name: string | null;
};

export type SellerProfileSummary = {
    total_paid_lifetime: number;
    total_pipeline: number;
    paid_mtd: number;
    paid_ytd: number;
    accrued_mtd: number;
    awaiting_nf_amount: number;
    awaiting_nf_count: number;
    nf_submitted_amount: number;
    nf_submitted_count: number;
    records_total: number;
    deals_total: number;
};

export type SellerProfileMonth = {
    month: string;
    paid: number;
    accrued: number;
    pipeline: number;
    deals: number;
};

export type SellerHistoryMonthItem = {
    record_id: number;
    bucket: 'paid' | 'accrued' | string;
    deal_id: number;
    deal_number: string | null;
    internal_number?: string | null;
    display_sale_number?: string | null;
    /** Pedidos/propostas com valor — 2+ quando a venda tem propostas irmãs. */
    sale_numbers?: string[];
    deal_title: string | null;
    deal_amount: number | null;
    finish_date?: string | null;
    commission_installment_id?: number | null;
    installment_number?: number | null;
    entry_index?: number | null;
    total_installments?: number | null;
    due_date?: string | null;
    client_paid_at?: string | null;
    installment_amount?: number | null;
    /** SDR: comissão base do vendedor (sobre a qual cai a fatia). */
    basis_amount?: number | null;
    /** `sdr_owner_base` = taxa é % da base do vendedor, não do valor da coluna. */
    /** `team_agreement_pool` = taxa exibida é a base do pool; hint mostra % TA. */
    rate_basis?: 'movement' | 'sdr_owner_base' | 'team_agreement_pool' | string;
    commission_rate_pp?: number | null;
    /** Taxa cheia do pool (antes do rateio TA). */
    pool_rate_pp?: number | null;
    owner_share_pct?: number | null;
    collaborator_share_pct?: number | null;
    commission_role: string;
    role_label: string;
    /** Colaborador: ela + responsável (avatars na coluna Papel). */
    role_parties?: Array<{
        id?: number | null;
        name: string;
        avatar_url?: string | null;
        role: string;
    }>;
    /** Vendedor responsável da venda (relatório de gerente/diretor/admin). */
    sale_seller?: {
        id?: number | null;
        name: string;
        avatar_url?: string | null;
    } | null;
    /** Gerente da venda (visão diretor / super_admin). */
    sale_manager?: {
        id?: number | null;
        name: string;
        avatar_url?: string | null;
    } | null;
    final_amount: number;
    status: string;
    status_label: string;
    /** Competência do vendedor (mês seguinte ao pagamento do cliente). */
    paid_at?: string | null;
    is_legacy_frozen?: boolean;
    is_manual_inclusion?: boolean;
    manual_inclusion_id?: number | null;
    /** Só vem no payload para super_admin. */
    manual_audit?: {
        included_by_id?: number | null;
        included_by_name?: string | null;
        included_at?: string | null;
        notes?: string | null;
    } | null;
    mode_flags?: Array<{ key: string; label: string }>;
    is_defaulted?: boolean;
    is_forfeited?: boolean;
    is_retroactive?: boolean;
    /** Link SPA do Hub (/ploomes-deals/…). */
    hub_url?: string | null;
    /** URL externa do deal no Ploomes. */
    ploomes_url?: string | null;
    default_event?: {
        id: number;
        status: string;
        flagged_at?: string | null;
        flagged_by_name?: string | null;
        flag_reason?: string | null;
        cleared_at?: string | null;
        cleared_by_name?: string | null;
        clear_reason?: string | null;
        client_paid_at?: string | null;
        days_defaulted?: number | null;
        installment_amount_expected?: number | null;
        installment_amount_received?: number | null;
        payment_coverage?: string | null;
        payment_shortfall?: number | null;
        commission_amount_blocked?: number | null;
        commission_amount_paid?: number | null;
        original_competence?: string | null;
        recovery_competence?: string | null;
    } | null;
    days_overdue?: number | null;
};

export type HistoryPartyColumn = 'seller' | 'manager';

export type HistoryFilterParty = {
    id?: number | null;
    name: string;
    avatar_url?: string | null;
};

export type SellerHistoryMonthDetail = {
    month: string;
    /** Colunas extras: gerente vê vendedor; diretor/admin vê vendedor + gerente. */
    party_columns?: HistoryPartyColumn[];
    /** Roster da equipe liderada (filtro multi-select). */
    filter_parties?: {
        sellers: HistoryFilterParty[];
        managers: HistoryFilterParty[];
    };
    /** Comissões do mês que contam (já sem inadimplentes). */
    paid_total: number;
    normal_total?: number;
    retroactive_total?: number;
    retroactive_count?: number;
    retroactive_items?: SellerHistoryMonthItem[];
    /** Valor completo = paid_total + defaulted_total. */
    gross_paid_total?: number;
    accrued_total: number;
    paid_count: number;
    accrued_count: number;
    paid_deals: number;
    accrued_deals: number;
    paid_items: SellerHistoryMonthItem[];
    accrued_items: SellerHistoryMonthItem[];
    /** Inadimplências com competência neste mês (abatidas do bruto). */
    defaulted_total?: number;
    defaulted_count?: number;
    defaulted_items?: SellerHistoryMonthItem[];
    /** Perdidas (3 meses) — informativo, fora do líquido. */
    forfeited_total?: number;
    forfeited_count?: number;
    forfeited_items?: SellerHistoryMonthItem[];
    /** Líquido a pagar (= paid_total = gross − defaulted). */
    net_paid_total?: number;
    wallet?: {
        balance: number;
        wallet_applied_total?: number;
        entries?: Array<{ id: number; type: string; amount: number; note?: string | null; deal_number?: string | null; commission_installment_id?: number | null }>;
        allocatable_deals?: Array<{ ploomes_deal_id: number; title?: string | null; sale_number?: string | null }>;
    };
    wallet_applied_total?: number;
    paid_total_after_wallet?: number;
    wallet_applied_items?: WalletAppliedItem[];
    wallet_excess?: number;
    /** Preenchido quando a competência foi fechada (valores vindos do snapshot). */
    closing?: MonthClosingInfo | null;
};

export type WalletAppliedItem = {
    id: number;
    kind: 'sale_cancel' | 'moved' | 'reallocated' | 'allocation' | `deal_${string}`;
    amount: number;
    /** Valor lançado originalmente no mês (antes de remanejar). */
    original_amount?: number;
    /** Partes que já saíram deste estorno para outros meses. */
    moved_out?: Array<{ competence_month: string | null; amount: number; reason?: string | null; created_at?: string | null }>;
    /** Estorno remanejado: mês e tipo do estorno de origem. */
    origin_month?: string | null;
    origin_kind?: string | null;
    reason?: string | null;
    note?: string | null;
    ploomes_deal_id?: number | null;
    commission_installment_id?: number | null;
    deal_title?: string | null;
    deal_number?: string | null;
    commission_role?: string | null;
};

export type MonthClosingInfo = {
    competence: string;
    closed_at?: string | null;
    closed_by?: string | null;
    notes?: string | null;
    snapshot_status: 'pending' | 'done' | 'failed';
};

export type SellerInstallmentPipeline = {
    installments_remaining: number;
    installments_considered_paid: number;
    installments_total: number;
    commission_pending: number;
    commission_received: number;
    commission_cancelled?: number;
    commission_total: number;
    forecast: SellerCommissionForecast;
    deals: Array<{
        deal_id: number;
        deal_number: string | null;
        internal_number?: string | null;
        display_sale_number?: string | null;
        deal_title: string | null;
        deal_amount: number;
        payment_parcelas: number | null;
        installment_label: string | null;
        installments_remaining: number;
        commission_rate_pp: number;
        seller_commission_per_installment: number;
        seller_commission_pending: number;
        seller_commission_received: number;
        seller_commission_cancelled?: number;
        seller_commission_total: number;
    }>;
};

export type SellerForecastInstallmentItem = {
    deal_id: number;
    deal_number: string | null;
    internal_number?: string | null;
    display_sale_number?: string | null;
    deal_title: string | null;
    deal_amount: number;
    installment_id: number;
    installment_number: number;
    entry_index?: number;
    total_installments: number;
    due_date: string | null;
    installment_amount: number;
    commission_amount: number;
    commission_rate_pp: number;
    status: string;
    is_overdue?: boolean;
};

export type SellerCommissionForecastMonth = {
    month: string;
    amount: number;
    installments_count: number;
    cumulative: number;
    deals_count?: number;
    items?: SellerForecastInstallmentItem[];
};

export type SellerCommissionForecast = {
    months: SellerCommissionForecastMonth[];
    horizon_months?: number;
    total_remaining: number;
    horizon_total?: number;
    installments_count: number;
    horizon_installments_count?: number;
};

export type SellerDefaultedInstallment = {
    installment_id: number;
    deal_id: number;
    deal_number: number | null;
    internal_number?: string | null;
    display_sale_number?: string | null;
    deal_title: string | null;
    client_name: string | null;
    installment_number: number;
    total_installments: number;
    due_date: string | null;
    days_overdue: number | null;
    installment_amount: number;
    commission_amount: number;
    commission_rate_pp: number;
    status: 'defaulted' | 'overdue';
};

export type SellerDefaultedInstallments = {
    count: number;
    installment_amount_total: number;
    commission_blocked_total: number;
    items: SellerDefaultedInstallment[];
};

export type SellerCommissionProfile = {
    user: SellerProfileUser;
    team: SellerProfileTeam | null;
    summary: SellerProfileSummary;
    monthly: SellerProfileMonth[];
    installment_pipeline: SellerInstallmentPipeline;
    defaulted_installments?: SellerDefaultedInstallments;
    records: {
        upcoming: CommissionRecord[];
        paid: CommissionRecord[];
        all: CommissionRecord[];
    };
};

export type CommissionPeriodType = 'month' | 'bimonth' | 'quarter' | 'quadrimester' | 'semester' | 'year';

export type CommissionBreakdownItem = {
    seller_id: number;
    seller_name: string;
    total: number;
    records_count: number;
    is_own: boolean;
};

export type CommissionDealItem = {
    deal_id: number;
    deal_number: number;
    title: string;
    finish_date: string | null;
    final_amount: number;
    status: string;
};

export type CommissionOrgChartPerson = {
    user_id: number;
    name: string;
    avatar_url: string | null;
    commission_role: CommissionRole;
    role_label: string;
    total: number;
    paid: number;
    pending: number;
    records_count: number;
    rate_hint?: string | null;
    breakdown?: CommissionBreakdownItem[];
    deals?: CommissionDealItem[];
};

export type FunctionalOrgBranch = {
    team_name: string;
    manager: CommissionOrgChartPerson;
    members: CommissionOrgChartPerson[];
    team_total: number;
    rate_hint: string;
};

export type FunctionalOrgPillar = {
    label: string;
    rate_hint: string | null;
    people: CommissionOrgChartPerson[];
    total: number;
};

export type FunctionalOrgChartData = {
    president: (CommissionOrgChartPerson & { rate_hint?: string }) | null;
    manager_branches: FunctionalOrgBranch[];
    pillars: FunctionalOrgPillar[];
};

export type OrgChartTeam = {
    id: number | null;
    name: string;
    commission_mode: string;
    inferred?: boolean;
    directors: CommissionOrgChartPerson[];
    managers: CommissionOrgChartPerson[];
    members: CommissionOrgChartPerson[];
    team_total: number;
};

export type CommissionOrgChartCoverage = {
    deals_won: number;
    deals_with_commission: number;
    deals_missing_commission: number;
    deals_coverage_pct: number;
    distinct_deals_in_records: number;
    paid_installments: number;
    paid_installments_with_cascade: number;
    paid_installments_missing_cascade: number;
    cascade_coverage_pct: number;
    is_complete: boolean;
};

export type CommissionOrgChartData = {
    period: {
        type: CommissionPeriodType;
        type_label: string;
        year: number;
        index: number | null;
        from: string;
        to: string;
        label: string;
    };
    dating?: 'seller_pay_competence' | string;
    dating_label?: string;
    grand_total: number;
    grand_paid?: number;
    grand_pending?: number;
    executives: CommissionOrgChartPerson[];
    teams: OrgChartTeam[];
    unassigned: CommissionOrgChartPerson[];
    has_teams: boolean;
    coverage?: CommissionOrgChartCoverage;
    functional: FunctionalOrgChartData;
};

export type BucklerOneProgress = {
    network_units_count: number;
    total_deals: number;
    buckler_deals: number;
    total_sales_brl: number;
    buckler_sales_brl: number;
    buckler_pct_by_value: number;
    buckler_pct_by_count: number;
    meets_min_buckler_pct: boolean;
    target_total_brl: number;
    remaining_to_target_brl: number;
    progress_pct: number | null;
    min_buckler_sales_required_brl: number;
    remaining_buckler_for_min_pct_brl: number;
    machines_to_target: number | null;
    machines_for_min_pct: number | null;
    estimated_cashback_brl: number;
};

export type BucklerOnePartnership = {
    id: number;
    client_id: number;
    franchisor: {
        id: number;
        name: string;
        legal_name: string | null;
        logo_url: string | null;
    } | null;
    contract_started_at: string;
    target_total_brl: number;
    min_buckler_unit_pct: number;
    cashback_rate: number;
    reference_machine_value_brl: number | null;
    conditions_notes: string | null;
    cashback_notes: string | null;
    min_cashback_paid: boolean;
    min_cashback_paid_amount_brl: number | null;
    min_cashback_paid_at: string | null;
    active: boolean;
    progress: BucklerOneProgress;
};

export type FranchisorOption = {
    id: number;
    name: string;
    legal_name: string | null;
    logo_url: string | null;
    units_count: number;
    active: boolean;
};

export type GluteleaderBeneficiary = {
    key: string;
    name: string;
    email: string | null;
    avatar_url: string | null;
    user_id: number | null;
    rate: number;
    rate_pct: number;
    amount: number;
    amount_generated: number;
    amount_ytd: number;
    amount_accrued: number;
    amount_paid: number;
    amount_pending: number;
    share_pct: number;
    gross_base: number;
    quarter_closed: boolean;
};

export type GluteleaderRoyaltyProduct = {
    id: number;
    product_id: number;
    code: string | null;
    name: string;
    line: string | null;
    series: string | null;
    supplier: string | null;
    unit_price: number | null;
    image_url: string | null;
    sales_scope: 'all' | 'from_date' | 'from_now';
    sales_scope_label: string;
    sales_scope_short: string;
    effective_from: string | null;
    is_active: boolean;
    created_at: string | null;
};

export type GluteleaderProductSummary = {
    registry_id: number | null;
    product_id: number | null;
    code: string | null;
    name: string;
    line: string | null;
    series: string | null;
    supplier: string | null;
    unit_price: number | null;
    image_url: string | null;
    quantity_sold: number;
    revenue: number;
    deals_count: number;
    share_pct: number;
    has_sales: boolean;
    in_catalog: boolean;
    sales_scope: 'all' | 'from_date' | 'from_now' | null;
    sales_scope_label: string | null;
    sales_scope_short: string | null;
    effective_from: string | null;
};

export type GluteleaderDashboard = {
    period: {
        year: number;
        quarter: number;
        scope: 'year' | 'quarter';
        label: string;
        start: string;
        end: string;
    };
    kpis: {
        gross_total: number;
        gross_ytd: number;
        commission_generated: number;
        commission_accrued: number;
        commission_paid: number;
        commission_pending: number;
        deals_count: number;
        lines_count: number;
        catalog_count: number;
        products_sold_count: number;
        quantity_sold: number;
        quarter_closed: boolean;
        quarter_ended: boolean;
        needs_close_alert: boolean;
        vs_prev_quarter: {
            prev_year: number;
            prev_quarter: number;
            prev_gross: number;
            prev_commission: number;
            gross_pct: number | null;
            commission_pct: number | null;
        } | null;
    };
    beneficiaries: GluteleaderBeneficiary[];
    by_month: {
        labels: string[];
        gross: number[];
        commission_generated: number[];
        commission_lala: number[];
        commission_luiz_salles: number[];
        cumulative_gross: number[];
    };
    by_quarter: {
        quarter: number;
        label: string;
        gross: number;
        commission_generated: number;
        closed: boolean;
        period_key: string;
    }[];
    payouts: {
        record_id: number;
        period: string | null;
        beneficiary_key: string | null;
        beneficiary_name: string;
        amount: number;
        status: string;
        paid_at: string | null;
        created_at: string;
    }[];
    top_products: { label: string; total: number; count: number }[];
    top_sellers: { label: string; total: number; count: number }[];
    products_summary: GluteleaderProductSummary[];
    registry: GluteleaderRoyaltyProduct[];
    lines: {
        deal_id: number;
        deal_number: string | null;
        deal_title: string | null;
        finish_date: string | null;
        owner_name: string | null;
        product_id: number | null;
        product_name: string | null;
        product_code: string | null;
        quantity: number;
        line_total: number;
    }[];
};

// ─── Metas por Bucket (Q3 Spiff Bonus) ───────────────────────────────────────

export type CommissionBucketKey = 'force' | 'cardio' | 'gluteleader' | 'accessories' | 'other';

export type CommissionGoalBucketRow = {
    bucket: CommissionBucketKey;
    label: string;
    color: string;
    weight: number;
    target: number;
    achieved: number;
    pct: number;
};

export type CommissionGoalPerformance = {
    period: string;
    global_target: number;
    fixed_salary: number;
    is_exempt_spiff: boolean;
    buckets: CommissionGoalBucketRow[];
    global_achieved: number;
    pct_global: number;
    reached_100: boolean;
    active_buckets: CommissionBucketKey[];
    is_only_force_cardio: boolean;
    passes_diversification: boolean;
    earns_bonus: boolean;
    bonus_amount: number;
};

export type CommissionGoal = {
    id: number;
    user_id: number;
    user_name: string;
    user_email: string;
    user_avatar_url: string | null;
    period: string;
    source_period?: string;
    is_inherited?: boolean;
    global_target: number;
    fixed_salary: number;
    bucket_weights: Record<CommissionBucketKey, number>;
    is_exempt_spiff: boolean;
    notes: string | null;
    performance?: CommissionGoalPerformance;
};

/** Detalhe da NF mensal reutiliza o mesmo shape rico do payment approval. */
export type CommissionMonthlyNfRecord = PaymentApprovalNfRecord;

export type CommissionMonthlyNf = {
    id: number;
    period: string;
    period_label: string;
    user_id: number;
    user_name: string;
    status: string;
    status_label: string;
    total_commissions: number;
    total_salary: number;
    total_amount: number;
    records_count: number;
    composition?: CommissionComposition;
    invoice_path: string | null;
    invoice_url: string | null;
    invoice_submitted_at: string | null;
    nf_requested_at: string | null;
    nf_deadline_at: string | null;
    days_until_deadline: number | null;
    paid_at: string | null;
    paid_by_name: string | null;
    notes: string | null;
    created_at: string;
    records?: CommissionMonthlyNfRecord[];
};
